import { useState, useEffect } from "react";
import Button from "../../Components/Button/Button";
import TextField from "../../Components/TextField/TextField";
import Dropdown from "../../Components/Dropdown/Dropdown";
import { useToast } from "../../Contexts/ToastContext/ToastContext";
import { useAuth } from "../../Contexts/AuthContext/AuthContext";
import api from "../../services/api";
import "./Locations.css";

const Locations = () => {
  const { showToast } = useToast();
  const { isAdmin, isManagerOrAdmin, hasAccessToLocation } = useAuth();

  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit] = useState(20);
  const [totalLocations, setTotalLocations] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showWarehouseAssignModal, setShowWarehouseAssignModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const [locationForm, setLocationForm] = useState({
    name: "",
    address: ""
  });

  const [showMoveTagsModal, setShowMoveTagsModal] = useState(false);
  const [selectedMoveRoomRid, setSelectedMoveRoomRid] = useState(null);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const data = await api.getLocations(currentPage, pageLimit);
      setLocations(data.locations || []);
      setTotalLocations(data.totalLocations || data.locations?.length || 0);
    } catch (error) {
      showToast(error.message || "Failed to fetch locations", 3000, true, "danger");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const data = await api.getWarehouses(1, 100);
      setWarehouses(data.warehouses || []);
    } catch (error) {
      console.error("Failed to fetch warehouses:", error);
    }
  };

  const fetchAllRooms = async () => {
    try {
      const data = await api.getRooms({ forTagAssignment: true, limit: 1000 });
      setAllRooms(data.rooms || []);
    } catch (error) {
      console.error("Failed to fetch rooms:", error);
    }
  };

  const createLocation = async () => {
    if (!locationForm.name) {
      showToast("Location name is required", 2000, true, "danger");
      return;
    }
    if (!locationForm.address?.trim()) {
      showToast("Location address is required", 2000, true, "danger");
      return;
    }

    try {
      await api.createLocation(locationForm);
      showToast("Location created successfully!", 2000, true, "success");
      setShowLocationModal(false);
      resetLocationForm();
      fetchLocations();
    } catch (error) {
      showToast(error.message || "Failed to create location", 2000, true, "danger");
      console.error(error);
    }
  };

  const updateLocation = async () => {
    if (!locationForm.name) {
      showToast("Location name is required", 2000, true, "danger");
      return;
    }
    if (!locationForm.address?.trim()) {
      showToast("Location address is required", 2000, true, "danger");
      return;
    }

    try {
      await api.updateLocation(editingLocation._id, locationForm);
      showToast("Location updated successfully!", 2000, true, "success");
      setShowLocationModal(false);
      setEditingLocation(null);
      resetLocationForm();
      fetchLocations();
    } catch (error) {
      showToast(error.message || "Failed to update location", 2000, true, "danger");
      console.error(error);
    }
  };

  const deleteLocation = async (tagAction = "delete", moveTagsToRid = null) => {
    if (!deleteTarget) return;

    try {
      await api.deleteLocation(deleteTarget._id, {
        tagAction,
        moveTagsToRid,
      });
      showToast("Location deleted successfully!", 2000, true, "success");
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
      fetchLocations();
    } catch (error) {
      showToast(error.message || "Failed to delete location", 2000, true, "danger");
      console.error(error);
    }
  };
  const handleMoveTagsOnLocationDelete = () => {
    if (!deleteTarget) return;
    setSelectedMoveRoomRid(null);
    setShowMoveTagsModal(true);
  };

  const confirmMoveTagsOnLocationDelete = () => {
    if (!selectedMoveRoomRid) {
      showToast("Please select a target room", 2000, true, "danger");
      return;
    }
    deleteLocation("move", selectedMoveRoomRid);
    setShowMoveTagsModal(false);
  };

  const assignWarehouse = async (warehouseId) => {
    if (!selectedLocation) return;

    try {
      await api.assignWarehouseToLocation(selectedLocation._id, warehouseId);
      showToast("Warehouse assigned successfully!", 2000, true, "success");

      // Fetch updated data
      const [locData, whData] = await Promise.all([
        api.getLocations(currentPage, pageLimit),
        api.getWarehouses(1, 100)
      ]);

      setLocations(locData.locations || []);
      setWarehouses(whData.warehouses || []);

      // Update selected location with new data
      const updatedLocation = locData.locations?.find(l => l._id === selectedLocation._id);
      if (updatedLocation) {
        setSelectedLocation(updatedLocation);
      }
    } catch (error) {
      showToast(error.message || "Failed to assign warehouse", 2000, true, "danger");
      console.error(error);
    }
  };

  const resetLocationForm = () => {
    setLocationForm({
      name: "",
      address: ""
    });
  };

  useEffect(() => {
    fetchLocations();
    fetchWarehouses();
    fetchAllRooms();
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [currentPage]);

  const filteredLocations = locations.filter(location =>
    location.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    location.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(totalLocations / pageLimit);

  const getAvailableWarehouses = () => {
    if (!selectedLocation) return warehouses;
    const assignedIds = selectedLocation.warehouses?.map(w => w._id || w) || [];
    return warehouses.filter(w => !assignedIds.includes(w._id));
  };

  const getAssignedWarehouses = () => {
    if (!selectedLocation) return [];
    return selectedLocation.warehouses || [];
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Locations</h1>
            <p className="dashboard-subtitle">Manage physical locations and their warehouses</p>
          </div>
          <div className="dfcc gap-2">
            {isAdmin() && (
              <Button
                value="Add Location"
                iconLeft="fa-plus"
                onClick={() => {
                  setEditingLocation(null);
                  resetLocationForm();
                  setShowLocationModal(true);
                }}
                variant="primary"
                padding="12px 24px"
              />
            )}
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#e8f5e9" }}>
              <i className="fa fa-map-marker" style={{ color: "#4CAF50" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{totalLocations}</h3>
              <p className="stat-label">Total Locations</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#fff3e0" }}>
              <i className="fa fa-building" style={{ color: "#FF9800" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">{warehouses.length}</h3>
              <p className="stat-label">Total Warehouses</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ backgroundColor: "#e3f2fd" }}>
              <i className="fa fa-link" style={{ color: "#2196F3" }}></i>
            </div>
            <div className="stat-content">
              <h3 className="stat-value">
                {locations.reduce((acc, loc) => acc + (loc.warehouses?.length || 0), 0)}
              </h3>
              <p className="stat-label">Assigned Warehouses</p>
            </div>
          </div>

      
        </div>

        <div className="filters-section">
          <h2 className="section-title">Search Locations</h2>
          <div className="filters-grid">
            <TextField
              placeholder="Search by name or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              iconLeft="fa-search"
              variant="outlined"
            />
            <Button
              value="Clear"
              iconLeft="fa-times"
              onClick={() => setSearchTerm("")}
              variant="outline"
              padding="12px 24px"
            />
          </div>
        </div>

        <div className="rooms-section">
          <h2 className="section-title">Locations</h2>
          <div className="rooms-grid">
            {loading ? (
              <div className="loading-state" style={{ gridColumn: "1/-1" }}>
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "48px", color: "#4CAF50" }}></i>
                <p>Loading locations...</p>
              </div>
            ) : filteredLocations.length === 0 ? (
              <div className="empty-state" style={{ gridColumn: "1/-1" }}>
                <i className="fa fa-map-marker" style={{ fontSize: "48px", color: "#ccc" }}></i>
                <p>No locations found</p>
              </div>
            ) : (
              filteredLocations.map((location) => (
                <div key={location._id} className="room-card">
                  <div className="room-header">
                    <div className="room-icon" style={{
                      background: "linear-gradient(135deg, #C8E6C9 0%, #A5D6A7 100%)",
                      color: "#2E7D32"
                    }}>
                      <i className="fa fa-map-marker"></i>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 className="room-title">{location.name}</h3>
                      <p className="room-id">{location.address || "No address"}</p>
                    </div>
                    {(isAdmin() || hasAccessToLocation(location._id)) && (
                      <div className="room-actions">
                        <button
                          className="room-action-btn edit"
                          onClick={() => {
                            setEditingLocation(location);
                            setLocationForm({
                              name: location.name,
                              address: location.address || ""
                            });
                            setShowLocationModal(true);
                          }}
                          title="Edit Location"
                        >
                          <i className="fa fa-edit"></i>
                        </button>
                        {isAdmin() && (
                          <button
                            className="room-action-btn delete"
                            onClick={() => {
                              setDeleteTarget(location);
                              setShowDeleteConfirm(true);
                            }}
                            title="Delete Location"
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="room-stats">
                    <div className="room-stat">
                      <i className="fa fa-building"></i>
                      <span>{location.warehouses?.length || 0} Warehouses</span>
                    </div>
                  </div>
                  <Button
                    value="Manage Warehouses"
                    iconRight="fa-cogs"
                    onClick={() => {
                      setSelectedLocation(location);
                      setShowWarehouseAssignModal(true);
                    }}
                    variant="outline"
                    width="100%"
                    padding="10px 16px"
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {!loading && totalLocations > pageLimit && (
          <div className="pagination" style={{ background: "white", padding: "16px 24px", borderRadius: "16px", border: "1px solid #E2E8F0" }}>
            <Button
              value="Previous"
              iconLeft="fa-chevron-left"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              variant="outline"
              padding="8px 16px"
            />
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <Button
              value="Next"
              iconRight="fa-chevron-right"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              variant="outline"
              padding="8px 16px"
            />
          </div>
        )}
      </div>

      {showLocationModal && (
        <div className="modal-overlay" onClick={() => setShowLocationModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ background: "linear-gradient(135deg, #C8E6C9 0%, #A5D6A7 100%)", color: "#2E7D32" }}>
                <i className="fa fa-map-marker"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">{editingLocation ? "Edit Location" : "Add New Location"}</span>
                <span className="modal-subtitle">
                  {editingLocation ? "Update location information" : "Create a new physical location"}
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowLocationModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Location Name</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  placeholder="Enter location name"
                  value={locationForm.name}
                  onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                  variant="outlined"
                />
              </div>

              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Address</span>
                  <span className="form-label-required">*</span>
                </div>
                <TextField
                  placeholder="Enter location address"
                  value={locationForm.address}
                  onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                  variant="outlined"
                />
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn cancel-btn" onClick={() => setShowLocationModal(false)}>
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={editingLocation ? updateLocation : createLocation}
              >
                <i className={`fa ${editingLocation ? "fa-save" : "fa-plus"}`}></i>
                <span>{editingLocation ? "Update Location" : "Create Location"}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWarehouseAssignModal && selectedLocation && (
        <div className="modal-overlay" onClick={() => setShowWarehouseAssignModal(false)}>
          <div className="modal-container" style={{ maxWidth: "700px" }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle" style={{ background: "linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)", color: "#F57C00" }}>
                <i className="fa fa-building"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Manage Warehouses</span>
                <span className="modal-subtitle">
                  {selectedLocation.name} - Assign or remove warehouses
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowWarehouseAssignModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper" style={{ maxHeight: "60vh" }}>
              <div style={{ marginBottom: "24px" }}>
                <h4 style={{ margin: "0 0 12px 0", color: "#0F172A", fontSize: "14px", fontWeight: "600" }}>
                  Assigned Warehouses ({getAssignedWarehouses().length})
                </h4>
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {getAssignedWarehouses().length === 0 ? (
                    <p style={{ color: "#94a3b8", fontSize: "14px" }}>No warehouses assigned</p>
                  ) : (
                    getAssignedWarehouses().map((warehouse) => (
                      <div key={warehouse._id || warehouse} style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        backgroundColor: "#f0fdf4",
                        borderRadius: "10px",
                        border: "1px solid #bbf7d0"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <i className="fa fa-building" style={{ color: "#22c55e" }}></i>
                          <div>
                            <span style={{ fontWeight: "500", color: "#0F172A" }}>
                              {typeof warehouse === 'object' ? warehouse.name : warehouse}
                            </span>
                            <p style={{ margin: 0, fontSize: "11px", color: "#64748b" }}>
                              {typeof warehouse === 'object' ? `${warehouse.rooms?.length || 0} rooms` : ''}
                            </p>
                          </div>
                        </div>
                        <span style={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor: "#dcfce7",
                          color: "#16a34a",
                          fontSize: "11px",
                          fontWeight: "600"
                        }}>
                          <i className="fa fa-check" style={{ marginRight: "4px" }}></i>
                          Assigned
                        </span>
                      </div>
                    ))
                  )}
                </div>
                {getAssignedWarehouses().length > 0 && (
                  <p style={{
                    margin: "12px 0 0 0",
                    padding: "10px 12px",
                    backgroundColor: "#f0f9ff",
                    borderRadius: "8px",
                    border: "1px solid #bae6fd",
                    color: "#0369a1",
                    fontSize: "12px"
                  }}>
                    <i className="fa fa-info-circle" style={{ marginRight: "6px" }}></i>
                    To move a warehouse to another location, edit it from the Warehouses page. To remove it completely, delete the warehouse (this will delete all rooms and tags inside).
                  </p>
                )}
              </div>

              {(isAdmin() || hasAccessToLocation(selectedLocation._id)) && (
                <div>
                  <h4 style={{ margin: "0 0 12px 0", color: "#0F172A", fontSize: "14px", fontWeight: "600" }}>
                    Available Warehouses ({getAvailableWarehouses().length})
                  </h4>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {getAvailableWarehouses().length === 0 ? (
                      <p style={{ color: "#94a3b8", fontSize: "14px" }}>No warehouses available to assign</p>
                    ) : (
                      getAvailableWarehouses().map((warehouse) => (
                        <div key={warehouse._id} style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 16px",
                          backgroundColor: "#f8fafc",
                          borderRadius: "10px",
                          border: "1px solid #e2e8f0"
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                            <i className="fa fa-building" style={{ color: "#64748b" }}></i>
                            <span style={{ fontWeight: "500", color: "#0F172A" }}>{warehouse.name}</span>
                          </div>
                          <button
                            onClick={() => assignWarehouse(warehouse._id)}
                            style={{
                              padding: "6px 12px",
                              borderRadius: "6px",
                              border: "none",
                              backgroundColor: "#dbeafe",
                              color: "#3b82f6",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: "600"
                            }}
                          >
                            Assign
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer-wrapper">
              <div className="modal-action-btn primary-btn" onClick={() => setShowWarehouseAssignModal(false)}>
                <i className="fa fa-check"></i>
                <span>Done</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper delete-header">
              <div className="modal-icon-circle delete-icon-bg">
                <i className="fa fa-exclamation-triangle"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title delete-title">Delete Location</span>
                <span className="modal-subtitle delete-subtitle">
                  This action cannot be undone
                </span>
              </div>
              <div className="modal-close-btn delete-close" onClick={() => setShowDeleteConfirm(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper delete-body">
              <div className="delete-message-wrapper">
                <div className="delete-message-icon">
                  <i className="fa fa-map-marker"></i>
                </div>
                <div className="delete-message-content">
                  <span className="delete-message-primary">
                    Are you sure you want to delete <strong>"{deleteTarget.name}"</strong>?
                  </span>
                  <span className="delete-message-secondary">
                    This will remove the location and its warehouses. Choose what to do with tags in rooms under this location.
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer-wrapper" style={{ flexDirection: "column", alignItems: "stretch" }}>
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={() => deleteLocation("unassign")}
              >
                <i className="fa fa-unlink"></i>
                <span>Delete location (unassign tags)</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={handleMoveTagsOnLocationDelete}
              >
                <i className="fa fa-arrow-right"></i>
                <span>Move all tags to another room</span>
              </div>
              <div
                className="modal-action-btn delete-btn"
                onClick={() => deleteLocation("delete")}
              >
                <i className="fa fa-trash"></i>
                <span>Delete location and all tags</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMoveTagsModal && deleteTarget && (
        <div className="modal-overlay" onClick={() => setShowMoveTagsModal(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-wrapper">
              <div className="modal-icon-circle">
                <i className="fa fa-arrow-right"></i>
              </div>
              <div className="modal-title-section">
                <span className="modal-title">Move Tags to Another Partition</span>
                <span className="modal-subtitle">
                  Select a target partition for all tags under location "{deleteTarget.name}"
                </span>
              </div>
              <div className="modal-close-btn" onClick={() => setShowMoveTagsModal(false)}>
                <i className="fa fa-times"></i>
              </div>
            </div>

            <div className="modal-body-wrapper">
              <div className="form-field">
                <div className="form-label-wrapper">
                  <span className="form-label">Target Partition</span>
                  <span className="form-label-required">*</span>
                </div>
                <Dropdown
                  options={[
                    { label: "Select a partition", value: "" },
                    ...allRooms
                      .filter(r => {
                        const locId = r.locationId?._id || r.locationId;
                        return locId !== (deleteTarget._id || deleteTarget.id);
                      })
                      .map(r => {
                        const warehouse = warehouses.find(w => (w._id || w.id) === (r.warehouseId?._id || r.warehouseId));
                        const location = locations.find(l => (l._id || l.id) === (r.locationId?._id || r.locationId));
                        const warehouseName = warehouse?.name || "Unknown warehouse";
                        const locationName = location?.name || "Unknown location";
                        return {
                          value: r.rid,
                          label: `${r.roomTitle} (RID ${r.rid}) — ${warehouseName} / ${locationName}`,
                        };
                      }),
                  ]}
                  value={selectedMoveRoomRid || ""}
                  onChange={(val) => setSelectedMoveRoomRid(val || null)}
                  placeholder="Select a room"
                  size="md"
                  width="100%"
                />
              </div>
            </div>

            <div className="modal-footer-wrapper">
              <div
                className="modal-action-btn cancel-btn"
                onClick={() => setShowMoveTagsModal(false)}
              >
                <i className="fa fa-times"></i>
                <span>Cancel</span>
              </div>
              <div
                className="modal-action-btn primary-btn"
                onClick={confirmMoveTagsOnLocationDelete}
              >
                <i className="fa fa-check"></i>
                <span>Move Tags</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Locations;
